import React, {useState} from 'react';
import {Stage} from '../Stage';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {ApprovalStage as ApprovalStageType} from "../../../api/application/getApplicationDetails";
import {getIsCompleted, getSubtitle} from "./util";
import {useMutation} from "@apollo/client";
import {CHANGE_APPLICATION_STAGE_STATUS} from "../../../api/application/changeApplicationStageStatus";
import LoadingButton from "@mui/lab/LoadingButton";

type ApprovalStageProps = {
    nextStageTitle?: string,
    refetch: () => void,
    stage: ApprovalStageType,
}

export const ApprovalStage = (props: ApprovalStageProps) => {
    const {
        nextStageTitle,
        refetch,
        stage,
    } = props

    const [modalContent, setModalContent] = useState<undefined | 'close' | 'proceed'>()

    const [changeStatus, {loading}] = useMutation(CHANGE_APPLICATION_STAGE_STATUS)

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const onChangeStatus = async (newStatus: 'APPROVED' | 'REJECTED') => {
        const {errors} = await changeStatus({variables: {applicationStageId: stage.id, newStatus}})

        if (errors === undefined || errors.length === 0) {
            setModalContent(undefined)

            await refetch()
        }
    }

    const isCompleted = getIsCompleted(stage)

    const subtitle = getSubtitle(stage)

    return (
        <>
            <Stage
                icon={isCompleted ? <CheckCircleOutlineIcon color="success" /> : <AccessTimeIcon color="warning"/>}
                id={stage.id}
                subTitle={subtitle}
                title={stage.title}
            >
                <Typography>
                    {stage.description}
                </Typography>

                {stage.stageStatus === 'REJECTED' && (
                    <Typography sx={{ fontStyle: 'italic', textDecoration: 'underline' }}>
                        {stage.rejectionMessage}
                    </Typography>
                )}

                <Stack direction="row" gap="1rem" justifyContent="flex-end" marginTop="1rem">
                    <Button
                        disabled={isCompleted}
                        color="error"
                        variant="contained"
                        onClick={() => setModalContent('close')}
                    >
                        Close Application
                    </Button>

                    <Button
                        disabled={isCompleted}
                        color="success"
                        variant="contained"
                        onClick={() => setModalContent('proceed')}
                    >
                        Proceed to Next Stage
                    </Button>
                </Stack>
            </Stage>

            <Dialog
                fullScreen={fullScreen}
                open={modalContent !== undefined}
                onClose={() => setModalContent(undefined)}
            >
                {modalContent === 'close' ? (
                    <>
                        <DialogTitle>
                            Close Application
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Continuing will <strong>stop this application from being able to proceed!</strong> be 100% sure before proceeding. Close this application?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                color="error"
                                onClick={() => setModalContent(undefined)}
                                variant="contained"
                                disabled={loading}
                            >
                                No, Go Back
                            </Button>
                            <LoadingButton
                                color="success"
                                onClick={() => onChangeStatus('REJECTED')}
                                variant="contained"
                                disabled={loading}
                                loading={loading}
                            >
                                Yes, Close it
                            </LoadingButton>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogTitle>
                            Progress Application
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {nextStageTitle
                                    ? (<span>The next stage is <strong>{nextStageTitle}.</strong> </span>)
                                    : (<span>This is the last stage. </span>)
                                }
                                Are you sure you'd like to progress this application to the next stage?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                color="error"
                                onClick={() => setModalContent(undefined)}
                                variant="contained"
                                disabled={loading}
                            >
                                No, Go Back
                            </Button>
                            <LoadingButton
                                color="success"
                                onClick={() => onChangeStatus('APPROVED')}
                                variant="contained"
                                disabled={loading}
                                loading={loading}
                            >
                                Yes, Proceed
                            </LoadingButton>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    )
}
